import { createFileRoute, Link } from '@tanstack/react-router';
import * as React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Monitor,
  Activity,
  Database,
  TrendingUp,
  TrendingDown,
  Users,
  Clock,
  AlertCircle,
  ArrowRight,
  Loader2,
} from 'lucide-react';

import { PageHeader } from '@/components/layout/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  useOverview,
  useSessionsActivity,
  useTopTechnicians,
  type ActivityBucket,
  type OverviewStats,
} from '@/hooks/use-stats';
import { useTheme } from '@/components/layout/theme';
import { formatBytes, relativeTime } from '@/lib/utils';
import { format } from 'date-fns';

export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardPage,
});

function DashboardPage() {
  const overview = useOverview();
  const activity = useSessionsActivity();
  const topTechs = useTopTechnicians(5);

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="At-a-glance health of your remote support fleet."
      />

      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <KpiCard
          loading={overview.isLoading}
          icon={Monitor}
          label="Devices online"
          value={overview.data?.devicesOnline}
          sublabel={
            overview.data
              ? `${overview.data.devicesOnline} of ${overview.data.devicesTotal} total`
              : undefined
          }
          accent={overview.data && overview.data.devicesOnline > 0 ? 'success' : 'muted'}
          link="/devices"
        />
        <KpiCard
          loading={overview.isLoading}
          icon={Activity}
          label="Active sessions"
          value={overview.data?.sessionsActive}
          sublabel={overview.data?.sessionsActive ? 'Right now' : 'No live sessions'}
          accent={
            overview.data && overview.data.sessionsActive > 0 ? 'primary' : 'muted'
          }
          pulse={overview.data ? overview.data.sessionsActive > 0 : false}
          link="/sessions"
        />
        <KpiCard
          loading={overview.isLoading}
          icon={TrendingUp}
          label="Sessions today"
          value={overview.data?.sessionsToday}
          sublabel={
            overview.data ? deltaText(overview.data.sessionsToday, overview.data.sessionsYesterday) : undefined
          }
          accent="default"
        />
        <KpiCard
          loading={overview.isLoading}
          icon={Database}
          label="Traffic today"
          valueText={overview.data ? formatBytes(overview.data.bytesTransferredToday) : undefined}
          sublabel="Relayed bytes (in + out)"
          accent="default"
        />
      </div>

      {/* Pending devices alert */}
      {overview.data && overview.data.devicesPending > 0 && (
        <Card className="mb-6 border-warning/40 bg-warning/5">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-warning" />
              <div>
                <p className="text-sm font-medium">
                  {overview.data.devicesPending} device
                  {overview.data.devicesPending > 1 ? 's' : ''} pending approval
                </p>
                <p className="text-xs text-muted-foreground">
                  Review and approve them before sessions can start.
                </p>
              </div>
            </div>
            <Link
              to="/devices"
              search={{ status: 'PENDING_APPROVAL' }}
              className="inline-flex items-center gap-1 text-sm font-medium text-warning hover:underline"
            >
              Review
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Chart + top techs */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Session activity</CardTitle>
            <CardDescription>Sessions started in the last 24 hours, by hour (UTC).</CardDescription>
          </CardHeader>
          <CardContent>
            <ActivityChart loading={activity.isLoading} data={activity.data?.data ?? []} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top technicians</CardTitle>
            <CardDescription>Last 30 days, by session count.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {topTechs.isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (topTechs.data?.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No sessions in the last 30 days yet.
              </p>
            ) : (
              topTechs.data?.data.map((tech, i) => (
                <div key={tech.userId} className="flex items-center gap-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-xs font-medium tabular-nums">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">
                      {tech.firstName || tech.lastName
                        ? `${tech.firstName ?? ''} ${tech.lastName ?? ''}`.trim()
                        : tech.email}
                    </p>
                    <p className="font-mono text-2xs text-muted-foreground">
                      {tech.sessionsCount} session{tech.sessionsCount > 1 ? 's' : ''} ·{' '}
                      {formatDuration(tech.totalDurationSec)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// ============================================================
// KPI card
// ============================================================

interface KpiCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value?: number;
  valueText?: string;
  sublabel?: string;
  accent?: 'default' | 'success' | 'primary' | 'muted';
  pulse?: boolean;
  loading?: boolean;
  link?: string;
}

function KpiCard({ icon: Icon, label, value, valueText, sublabel, accent = 'default', pulse, loading, link }: KpiCardProps) {
  const display = valueText ?? (value !== undefined ? value.toLocaleString() : '—');

  const accentClass = {
    default: 'text-foreground',
    success: 'text-success',
    primary: 'text-primary',
    muted: 'text-muted-foreground',
  }[accent];

  const content = (
    <Card className="relative overflow-hidden transition-colors group hover:border-foreground/30">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-2xs uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          <Icon className={`h-4 w-4 ${accentClass} ${pulse ? 'animate-pulse-dot' : ''}`} />
        </div>
        <div className="font-display text-4xl tabular-nums tracking-tight">
          {loading ? <Loader2 className="h-7 w-7 animate-spin text-muted-foreground/40" /> : display}
        </div>
        {sublabel && (
          <p className="mt-1.5 text-xs text-muted-foreground">{sublabel}</p>
        )}
      </CardContent>
    </Card>
  );

  return link ? (
    <Link to={link} className="block">
      {content}
    </Link>
  ) : content;
}

function deltaText(today: number, yesterday: number): string {
  if (yesterday === 0) {
    return today > 0 ? 'No data for yesterday' : 'No sessions yesterday';
  }
  const delta = today - yesterday;
  const pct = Math.abs(Math.round((delta / yesterday) * 100));
  const sign = delta >= 0 ? '+' : '−';
  return `${sign}${pct}% vs yesterday`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mr = m % 60;
  return `${h}h ${mr}m`;
}

// ============================================================
// Activity chart (Recharts)
// ============================================================

function ActivityChart({ data, loading }: { data: ActivityBucket[]; loading: boolean }) {
  const { resolved } = useTheme();
  const isDark = resolved === 'dark';

  // Color tokens — read from CSS vars at render time would require getComputedStyle.
  // We hard-code resolved hsl values that match the design tokens here for stability.
  const accent = isDark ? 'hsl(187, 70%, 55%)' : 'hsl(187, 73%, 30%)';
  const grid = isDark ? 'hsl(220, 18%, 18%)' : 'hsl(40, 18%, 86%)';
  const muted = isDark ? 'hsl(220, 8%, 60%)' : 'hsl(220, 12%, 42%)';

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const formatted = data.map((d) => ({
    hour: d.hour,
    label: format(new Date(d.hour), 'HH:00'),
    sessions: d.sessions,
  }));

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={formatted} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id="dashboardActivityFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
              <stop offset="100%" stopColor={accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: muted, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace' }}
            stroke={grid}
            interval={3}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: muted, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace' }}
            stroke={grid}
            width={40}
          />
          <Tooltip
            cursor={{ stroke: accent, strokeWidth: 1, strokeDasharray: '3 3' }}
            contentStyle={{
              background: isDark ? 'hsl(220, 22%, 11%)' : 'hsl(40, 30%, 99%)',
              border: `1px solid ${grid}`,
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'IBM Plex Sans, sans-serif',
            }}
            labelStyle={{ color: muted, marginBottom: 4 }}
            itemStyle={{ color: accent }}
            formatter={(value: number) => [`${value} session${value !== 1 ? 's' : ''}`, '']}
          />
          <Area
            type="monotone"
            dataKey="sessions"
            stroke={accent}
            strokeWidth={2}
            fill="url(#dashboardActivityFill)"
            isAnimationActive
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
